import { z } from 'zod';
import { countryCodeSchema, currencyCodeSchema, decimalStringSchema, idSchema, isoDateSchema, isoDateTimeSchema, } from './common.js';
import { paginationMetaSchema, paginationQuerySchema } from './pagination.js';
const safeText = z
    .string()
    .trim()
    .max(2000)
    // XML 1.0 forbids these code points; rejecting them prevents malformed archived documents.
    // eslint-disable-next-line no-control-regex
    .regex(/^[^\u0000-\u0008\u000B\u000C\u000E-\u001F]*$/u, 'electronicInvoices.invalidText');
const amount = decimalStringSchema({ precision: 18, scale: 4 });
const quantity = decimalStringSchema({ precision: 18, scale: 6 });
const sha256 = z.string().regex(/^[a-f0-9]{64}$/u);
export const ELECTRONIC_INVOICE_FORMATS = ['FACTUR_X', 'XRECHNUNG_CII', 'XRECHNUNG_UBL'];
export const electronicInvoiceFormatSchema = z.enum(ELECTRONIC_INVOICE_FORMATS);
export const electronicInvoiceEndpointSchema = z
    .object({ value: safeText.min(1).max(200), scheme: z.string().regex(/^(?:\d{4}|EM)$/u) })
    .strict();
export const electronicInvoicePartySchema = z
    .object({
    name: safeText.min(1).max(200),
    street: safeText.min(1).max(200),
    postalCode: safeText.min(1).max(20),
    city: safeText.min(1).max(120),
    country: countryCodeSchema,
    vatId: safeText.nullable(),
    taxNumber: safeText.nullable(),
    registrationId: safeText.nullable(),
    endpoint: electronicInvoiceEndpointSchema.nullable(),
    contactName: safeText.nullable(),
    email: z.string().email().nullable(),
    phone: safeText.nullable(),
})
    .strict();
export const electronicInvoiceTaxCategorySchema = z.enum([
    'S',
    'Z',
    'E',
    'AE',
    'K',
    'G',
    'O',
    'L',
    'M',
]);
export const electronicInvoiceAllowanceSchema = z
    .object({
    charge: z.boolean(),
    reason: safeText.min(1),
    amount,
    category: electronicInvoiceTaxCategorySchema,
    rate: amount,
    exemptionReason: safeText.nullable(),
})
    .strict();
export const electronicInvoiceLineSchema = z
    .object({
    allowances: z
        .array(z.object({ charge: z.boolean(), reason: safeText.min(1), amount }).strict())
        .max(100)
        .default([]),
    id: safeText.min(1).max(64),
    name: safeText.min(1).max(200),
    description: safeText.nullable(),
    quantity,
    unit: z.string().regex(/^[A-Z0-9]{1,3}$/u),
    price: amount,
    grossPrice: amount.nullable(),
    priceDiscount: amount,
    priceBasis: quantity.default('1'),
    net: amount,
    category: electronicInvoiceTaxCategorySchema,
    rate: amount,
    exemptionReason: safeText.nullable(),
    orderLineId: safeText.nullable(),
    receiptLineId: safeText.nullable(),
})
    .strict();
export const electronicInvoiceSnapshotSchema = z
    .object({
    schemaVersion: z.literal(1),
    advanceDeductionNotes: z.array(z.string().min(1).max(4000)).max(100).optional(),
    advanceDeductions: z
        .array(z
        .object({
        number: safeText.min(1).max(120),
        date: isoDateSchema,
        gross: amount,
        net: amount,
        tax: amount,
        payments: z.array(z.object({ date: isoDateSchema, amount }).strict()),
        buckets: z.array(z.object({ rate: amount, gross: amount, net: amount, tax: amount }).strict()),
    })
        .strict())
        .max(100)
        .optional(),
    number: safeText.min(1).max(60),
    kind: z.enum(['INVOICE', 'CREDIT_NOTE']),
    issueDate: isoDateSchema,
    dueDate: isoDateSchema.nullable(),
    serviceDate: isoDateSchema.nullable(),
    currency: currencyCodeSchema,
    seller: electronicInvoicePartySchema,
    buyer: electronicInvoicePartySchema,
    buyerReference: safeText.nullable(),
    orderReference: safeText.nullable(),
    receiptReference: safeText.nullable(),
    correctionReference: z
        .object({ number: safeText.min(1), issueDate: isoDateSchema.nullable() })
        .nullable(),
    payment: z
        .object({
        meansCode: z.enum(['10', '30', '48', '58', '59', '97']),
        iban: safeText.nullable(),
        bic: safeText.nullable(),
        accountName: safeText.nullable(),
        terms: safeText.nullable(),
        cashDiscountPercent: amount,
        cashDiscountDays: z.number().int().min(0).max(365),
        reference: safeText.nullable(),
    })
        .strict(),
    lines: z.array(electronicInvoiceLineSchema).min(1).max(2000),
    allowances: z.array(electronicInvoiceAllowanceSchema).max(2000),
    taxes: z
        .array(z
        .object({
        category: electronicInvoiceTaxCategorySchema,
        rate: amount,
        base: amount,
        amount,
        exemptionReason: safeText.nullable(),
    })
        .strict())
        .min(1)
        .max(100),
    totals: z
        .object({
        lineNet: amount,
        allowances: amount,
        charges: amount,
        net: amount,
        tax: amount,
        gross: amount,
        prepaid: amount,
        rounding: amount,
        due: amount,
    })
        .strict(),
})
    .strict();
export const electronicInvoiceParametersSchema = z
    .object({
    buyerReference: safeText.max(200).nullable().optional(),
    buyerEndpoint: electronicInvoiceEndpointSchema.nullable().optional(),
    buyerRegistrationId: safeText.max(200).nullable().optional(),
    orderReference: safeText.max(200).nullable().optional(),
    receiptReference: safeText.max(200).nullable().optional(),
    exemptionReason: safeText.nullable().optional(),
})
    .strict();
export const electronicInvoiceSettingsSchema = z
    .object({
    version: z.number().int().min(1),
    customerFormats: z.record(z.enum(['NONE', ...ELECTRONIC_INVOICE_FORMATS])).default({}),
    enabledFormats: z.array(electronicInvoiceFormatSchema).min(1),
    defaultFormat: electronicInvoiceFormatSchema,
    sellerEndpoint: electronicInvoiceEndpointSchema.nullable(),
    sellerContactName: safeText.nullable(),
    transmission: z.enum(['FILES_ONLY', 'APPROVED_PLATFORM']),
    platformName: safeText.nullable(),
    platformContractConfirmed: z.boolean(),
    retentionYears: z.number().int().min(10).max(30),
})
    .strict()
    .refine((value) => value.enabledFormats.includes(value.defaultFormat), 'electronicInvoices.defaultFormatInvalid')
    .refine((value) => value.transmission !== 'APPROVED_PLATFORM' ||
    (value.platformContractConfirmed && value.platformName !== null), 'electronicInvoices.platformContractRequired');
export const electronicInvoiceValidationSchema = z.object({
    accepted: z.boolean(),
    validator: z.string(),
    version: z.string(),
    artifactChecksum: sha256,
    checkedAt: isoDateTimeSchema,
    reports: z.array(z.object({ name: z.string(), xml: z.string(), accepted: z.boolean() })),
    issues: z.array(z.object({
        rule: z.string(),
        severity: z.enum(['ERROR', 'WARNING']),
        message: z.string(),
        path: z.string().nullable(),
    })),
});
export const electronicInvoiceArtifactSchema = z.object({
    id: idSchema,
    documentId: idSchema,
    documentNumber: z.string(),
    format: electronicInvoiceFormatSchema,
    standardVersion: z.string(),
    snapshotHash: sha256,
    xmlChecksum: sha256,
    pdfChecksum: sha256.nullable(),
    validation: electronicInvoiceValidationSchema,
    createdAt: isoDateTimeSchema,
    downloadUrl: z.string(),
    xmlUrl: z.string(),
});
export const electronicInvoiceExportSchema = z.object({
    documentId: idSchema,
    format: electronicInvoiceFormatSchema,
});
export const electronicInvoiceLineMappingSchema = z.object({
    lineId: z.string().min(1).max(64),
    purchaseOrderItemId: idSchema.nullable(),
    goodsReceiptItemId: idSchema.nullable(),
});
export const electronicInvoiceCommitSchema = z
    .object({
    version: z.number().int().min(1),
    previewHash: sha256,
    supplierId: idSchema,
    purchaseOrderId: idSchema.nullable().optional(),
    lineMappings: z.array(electronicInvoiceLineMappingSchema).max(2000),
    vendorInvoiceId: idSchema.nullable().optional(),
    vendorInvoiceVersion: z.number().int().min(1).optional(),
    prepaymentAllocations: z
        .array(z
        .object({
        paymentId: idSchema,
        amount: decimalStringSchema({ precision: 18, scale: 4, min: 0.0001 }),
    })
        .strict())
        .max(200)
        .optional(),
})
    .strict();
export const electronicInvoicePrepaymentReviewSchema = z
    .object({
    version: z.number().int().min(1),
    previewHash: sha256,
    vendorInvoiceVersion: z.number().int().min(1),
    prepaymentAllocations: electronicInvoiceCommitSchema.shape.prepaymentAllocations
        .unwrap()
        .min(1),
})
    .strict();
export const electronicInvoiceImportSchema = z.object({
    id: idSchema,
    version: z.number().int(),
    status: z.enum(['REVIEW', 'IMPORTED']),
    fileName: z.string(),
    mimeType: z.string(),
    originalChecksum: sha256,
    xmlChecksum: sha256,
    previewHash: sha256,
    invoice: electronicInvoiceSnapshotSchema,
    validation: electronicInvoiceValidationSchema,
    suppliers: z.array(z.object({ id: idSchema, name: z.string(), vatId: z.string().nullable() })),
    duplicateVendorInvoiceId: idSchema.nullable(),
    vendorInvoiceId: idSchema.nullable(),
    vendorInvoiceVersion: z.number().int().nullable().default(null),
    supplierId: idSchema.nullable().default(null),
    createdAt: isoDateTimeSchema,
});
export const electronicInvoiceOverviewSchema = z.object({
    artifacts: z.array(electronicInvoiceArtifactSchema),
    imports: z.array(electronicInvoiceImportSchema),
    meta: paginationMetaSchema,
    importsMeta: paginationMetaSchema,
});
export const electronicInvoiceArchiveQuerySchema = paginationQuerySchema
    .omit({ sort: true })
    .extend({
    importsPage: z.coerce.number().int().min(1).default(1),
});
export const electronicInvoiceSetupSchema = z.object({
    ready: z.boolean(),
    xrechnungVersion: z.string(),
    facturXVersion: z.string(),
    checks: z.array(z.object({ name: z.string(), ready: z.boolean() })),
    sources: z.array(z.object({ label: z.string(), url: z.string().url() })),
});
export const electronicInvoiceLookupsSchema = z.object({
    prepaymentMode: z.enum(['LEGACY', 'NATIVE_LEDGER']),
    customers: z.array(z.object({ id: idSchema, name: z.string() })),
    purchaseOrders: z.array(z.object({
        id: idSchema,
        number: z.string(),
        items: z.array(z.object({ id: idSchema, name: z.string(), quantity: z.string() })),
    })),
    receipts: z.array(z.object({
        id: idSchema,
        number: z.string(),
        items: z.array(z.object({
            id: idSchema,
            name: z.string(),
            purchaseOrderItemId: idSchema.nullable(),
            quantity: z.string(),
        })),
    })),
    vendorDrafts: z.array(z.object({ id: idSchema, number: z.string(), version: z.number().int() })),
    vendorAdvances: z
        .array(z.object({
        id: idSchema,
        bookingDate: isoDateSchema,
        reference: z.string().nullable(),
        currency: currencyCodeSchema,
        unallocatedAmount: amount,
    }))
        .default([]),
});
export const electronicInvoiceDocumentParametersSchema = z.object({
    version: z.number().int().min(1),
    parameters: electronicInvoiceParametersSchema,
});
//# sourceMappingURL=electronic-invoice.js.map